"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const Client_1 = require("./Client");
const nanoid_1 = require("nanoid");
class ConsumeError extends Error {
    constructor(message, errors) {
        super(message);
        this.errors = errors;
    }
}
class ConsumerClient extends Client_1.Client {
    constructor(url, userId, producerId, logger) {
        super(url, producerId, userId, "consumer", logger);
        this.logger = logger;
        this.consumers = {};
        this.remoteProducerIds = new Set();
        this.on("close", (error) => {
            var _a;
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.log("[CONSUMER CLIENT] - on.close", Object.assign(Object.assign({}, this.loggerContext()), { error }));
            if (this.checkProducerAliveTimeout)
                clearTimeout(this.checkProducerAliveTimeout);
            if (this.consumeWatchdogTimeout)
                clearTimeout(this.consumeWatchdogTimeout);
            this.remoteProducerIds.clear();
        });
        this.consumeWatchdog();
    }
    init(transport) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.log("[CONSUMER CLIENT] - connecting to data channel for transport " + transport.id, this.loggerContext());
            return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                var _b, _c;
                try {
                    const dto = yield Client_1.Client.retryHelper(() => this.serverConsumeData({ producerId: `${this.roomId}_signaling` }));
                    (_b = this.logger) === null || _b === void 0 ? void 0 : _b.log("[CONSUMER CLIENT] - consuming data on transport " + transport.id + " with params " + JSON.stringify(dto), this.loggerContext());
                    const dataConsumer = yield transport.consumeData({
                        id: dto.id,
                        dataProducerId: dto.producerId,
                        sctpStreamParameters: dto.sctpStreamParameters,
                        label: dto.label,
                        protocol: dto.protocol
                    });
                    dataConsumer.on("message", data => {
                        var _a;
                        data = JSON.parse(data);
                        (_a = this.logger) === null || _a === void 0 ? void 0 : _a.log("[CONSUMER CLIENT] - onMessage on transport " + transport.id + " and channel " + dto.id, data);
                        if (data.type === "app-data") {
                            this.emit("data", data.data);
                        }
                        else if (data.type === "update-producers") {
                            this.checkProducerAlive(data.producers);
                        }
                    });
                    dataConsumer.on("open", () => {
                        var _a;
                        (_a = this.logger) === null || _a === void 0 ? void 0 : _a.log("[CONSUMER CLIENT] - datachannel opened on transport " + transport.id + " and channel" + dto.id, this.loggerContext());
                        this.checkProducerAlive([]);
                        resolve();
                    });
                    dataConsumer.on("close", () => __awaiter(this, void 0, void 0, function* () {
                        var _d, _e;
                        (_d = this.logger) === null || _d === void 0 ? void 0 : _d.log("[CONSUMER CLIENT] - datachannel closed on transport " + transport.id + " and channel " + dto.id, this.loggerContext());
                        try {
                            if (!["closed", "failed", "disconnected"].includes(transport.connectionState) && !this.closed) {
                                yield this.init(transport);
                            }
                        }
                        catch (e) {
                            (_e = this.logger) === null || _e === void 0 ? void 0 : _e.log("[CONSUMER CLIENT]: error retrying init on transport " + transport.id, Object.assign(Object.assign({}, this.loggerContext()), { error: e }));
                            this.close();
                        }
                    }));
                }
                catch (e) {
                    (_c = this.logger) === null || _c === void 0 ? void 0 : _c.log("[CONSUMER CLIENT]: ERROR - connecting to data channels on transport " + transport.id, { error: e });
                    reject(e);
                }
            }));
        });
    }
    consumeWatchdog() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.consumeRequest && (this.consumeRequest.video || this.consumeRequest.audio || this.consumeRequest.screen)) {
                yield this.doConsumeStream({ tracks: this.consumeRequest }).catch(ignored => { });
            }
            this.consumeWatchdogTimeout = setTimeout(() => this.consumeWatchdog(), 100);
        });
    }
    closeStream(producerId) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.log("[CONSUMER CLIENT] - closing stream", Object.assign({}, this.loggerContext()));
            const consumer = this.consumers[producerId];
            if (consumer) {
                consumer.close();
                yield this.serverCloseStream({ consumerId: consumer.id });
            }
            delete this.consumers[producerId];
        });
    }
    producersUpdated(producerIds) {
        const updated = producerIds.length !== this.remoteProducerIds.size ||
            producerIds.filter(newId => !this.remoteProducerIds.has(newId)).length > 0;
        this.remoteProducerIds.clear();
        producerIds.forEach(id => this.remoteProducerIds.add(id));
        Object.entries(this.consumers).forEach(([id, consumer]) => {
            if (!producerIds.includes(id)) {
                consumer.close();
            }
        });
        if (updated) {
            this.emit("producers-updated", {
                video: Array.from(this.remoteProducerIds).filter(id => id.endsWith("_video")).length > 0,
                audio: Array.from(this.remoteProducerIds).filter(id => id.endsWith("_audio")).length > 0,
                screen: Array.from(this.remoteProducerIds).filter(id => id.endsWith("_screen")).length > 0
            });
        }
    }
    checkProducerAlive(producerIds) {
        this.producersUpdated(producerIds);
        if (this.checkProducerAliveTimeout)
            clearTimeout(this.checkProducerAliveTimeout);
        this.checkProducerAliveTimeout = setTimeout(() => {
            this.producersUpdated([]);
        }, ConsumerClient.PEER_ALIVE_TIMEOUT);
    }
    detectProducerIds(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield ConsumerClient.retryHelper(() => {
                let res = [];
                let errors = [];
                for (let type of ["video", "audio", "screen"]) {
                    if (options.tracks[type] === true) {
                        const id = Array.from(this.remoteProducerIds).find(id => id.endsWith("_" + type));
                        if (!id) {
                            errors.push(new Error(`Could not find ${type} track to consume`));
                        }
                        else {
                            res.push(id);
                        }
                    }
                }
                if (errors.length > 0 && res.length == 0)
                    throw new ConsumeError("Could not determine producer IDs to consume.", errors);
                return Promise.resolve(res);
            }, options.retry);
        });
    }
    consumeStream(options) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.log("[CONSUMER CLIENT]: consumeStream " + JSON.stringify(options), this.loggerContext());
            return yield this.doConsumeStream(options);
        });
    }
    doConsumeStream(options) {
        return __awaiter(this, void 0, void 0, function* () {
            this.consumeRequest = options.tracks;
            this.consumeInProgress = this.consumeInProgress || Promise.resolve().then(() => __awaiter(this, void 0, void 0, function* () {
                try {
                    const transport = yield this.awaitConnected();
                    const producerIds = yield this.detectProducerIds(options);
                    const rtpCapabilities = this.device.rtpCapabilities;
                    if (!rtpCapabilities)
                        throw new Error("Cant receive device capabilities");
                    let result = yield Promise.all(producerIds.map((producerId) => __awaiter(this, void 0, void 0, function* () {
                        var _a;
                        try {
                            let consumer = this.consumers[producerId];
                            if (!consumer) {
                                const data = yield this.serverConsumeStream({
                                    producerId,
                                    rtpCapabilities
                                });
                                // workaround for transport.consume bug
                                if (!data.rtpParameters.mid) {
                                    data.rtpParameters.mid = nanoid_1.nanoid(10);
                                }
                                try {
                                    consumer = yield transport.consume(data);
                                }
                                catch (e) {
                                    (_a = this.logger) === null || _a === void 0 ? void 0 : _a.log("[CONSUMER CLIENT]: ERROR - consuming stream, will retry...", Object.assign(Object.assign({}, this.loggerContext()), { error: e }));
                                    yield this.serverCloseStream({ consumerId: consumer.id });
                                    throw e;
                                }
                                this.consumers[producerId] = consumer;
                                consumer.on("@close", () => {
                                    this.emit("streamClosed", consumer.track);
                                    consumer.close();
                                    delete this.consumers[producerId];
                                    if (Object.keys(this.consumers).length === 0) {
                                        this.emit("allStreamsClosed");
                                    }
                                });
                            }
                            return consumer.track;
                        }
                        catch (e) {
                            return e;
                        }
                    })));
                    const { tracks, errors } = result.reduce((out, item) => {
                        if (item instanceof Error)
                            out.errors.push(item);
                        else
                            out.tracks.push(item);
                        return out;
                    }, { tracks: [], errors: [] });
                    Object.keys(this.consumers).forEach(id => {
                        if (producerIds.includes(id))
                            return;
                        this.closeStream(id);
                    });
                    if (tracks.length === 0 && (options.tracks.audio || options.tracks.video || options.tracks.screen)) {
                        throw new ConsumeError("No tracks to consume.", errors);
                    }
                    const res = this.lastConsumedStream || new MediaStream();
                    const lastConsumedTracks = res.getTracks();
                    const tracksToRemove = lastConsumedTracks
                        .filter(t => !tracks.find(tr => tr.id == t.id));
                    const tracksToAdd = tracks
                        .filter(t => !lastConsumedTracks.find(tr => tr.id == t.id));
                    tracksToRemove.forEach(t => res.removeTrack(t));
                    tracksToAdd.forEach(t => res.addTrack(t));
                    return res;
                }
                finally {
                    this.consumeInProgress = undefined;
                }
            }));
            this.lastConsumedStream = yield this.consumeInProgress;
            return this.lastConsumedStream;
        });
    }
    loggerContext() {
        return {
            consumers: Object.entries(this.consumers).reduce((res, [id, consumer]) => {
                res[id] = {
                    track: {
                        id: consumer.track.id,
                        kind: consumer.track.kind,
                        label: consumer.track.label,
                        readyState: consumer.track.readyState,
                    }
                };
                return res;
            }, {}),
            consumeRequest: this.consumeRequest,
            lastConsumedStream: this.lastConsumedStream ? {
                id: this.lastConsumedStream.id,
                tracks: this.lastConsumedStream.getTracks().map(t => ({
                    id: t.id,
                    kind: t.kind,
                    label: t.label,
                    readyState: t.readyState,
                })),
            } : undefined,
            remoteProducerIds: [...this.remoteProducerIds]
        };
    }
}
exports.ConsumerClient = ConsumerClient;
ConsumerClient.PEER_ALIVE_TIMEOUT = 30000;
